import {
  ZIPTEAMS_API_BASE_URL,
  ZIPTEAMS_API_CLIENT_KEY,
  ZIPTEAMS_API_SECRET,
} from "../utils/constants";
import type {
  LiveAvatarSessionRequest,
  LiveAvatarSessionResponse,
} from "../components/live-avatar/types";

const trimTrailingSlash = (value: string): string => value.replace(/\/+$/, "");

const getGatewayBaseUrl = (): string => {
  const configuredBase = process.env.REACT_APP_GATEWAY_BASE_URL;
  if (configuredBase) return trimTrailingSlash(configuredBase);

  const apiBase = ZIPTEAMS_API_BASE_URL || "";
  return trimTrailingSlash(apiBase.replace(/\/api\/?$/, ""));
};

const SESSION_TOKEN_URL = `${getGatewayBaseUrl()}/api/v1/client/liveavatar/sessions`;

const getErrorMessage = (data: any): string =>
  data?.data?.message ||
  data?.message ||
  data?.error ||
  "Failed to create LiveAvatar session.";

const normalizeSessionResponse = (data: any): LiveAvatarSessionResponse => {
  const sessionData = data?.data || data || {};
  const sessionToken = sessionData?.sessionToken || sessionData?.session_token;
  const expiresInSeconds =
    sessionData?.expiresInSeconds || sessionData?.expires_in_seconds;

  if (!sessionToken) {
    throw new Error("Backend did not return a LiveAvatar session token.");
  }

  return {
    sessionToken,
    ...(expiresInSeconds ? { expiresInSeconds } : {}),
  };
};

class LiveAvatar {
  /**
   * Backend contract — `POST ${GATEWAY_BASE_URL}/api/v1/client/liveavatar/sessions`.
   *
   * The gateway resolves the authenticated user and realtime booking context,
   * holds the LiveAvatar private credentials, and returns `data.session_token`.
   *
   * The LiveAvatar Web SDK (`@heygen/liveavatar-web-sdk`) uses that token to
   * drive the full session lifecycle from the browser.
   */
  createSession = (
    payload: LiveAvatarSessionRequest,
  ): Promise<LiveAvatarSessionResponse> =>
    fetch(SESSION_TOKEN_URL, {
      method: "POST",
      credentials: "include",
      headers: {
        "Content-Type": "application/json",
        "api-client-key": String(ZIPTEAMS_API_CLIENT_KEY),
        "api-client-secret": String(ZIPTEAMS_API_SECRET),
      },
      body: JSON.stringify(payload),
    }).then(async (response) => {
      const text = await response.text();
      let data: any = {};
      try {
        data = text ? JSON.parse(text) : {};
      } catch (err) {
        if (!response.ok) {
          throw new Error(text || "Failed to create LiveAvatar session.");
        }
        throw err;
      }

      if (!response.ok) {
        throw new Error(getErrorMessage(data));
      }

      return normalizeSessionResponse(data);
    });
}

const LiveAvatarService = new LiveAvatar();
export default LiveAvatarService;
